"use client";

import { useState, useEffect, useCallback, useRef } from "react";
import { usePathname } from "next/navigation";
import Link from "next/link";
import { Menu, X, Send, Globe } from "lucide-react";
import { clsx } from "clsx";
import { useLocale } from "@/lib/useLocale";
import { t } from "@/lib/translations";

const navLinks = [
  { key: "nav.home", href: "/" },
  { key: "nav.products", href: "/products" },
  { key: "nav.certifications", href: "/certifications" },
  { key: "nav.shipping", href: "/shipping" },
  { key: "nav.faq", href: "/faq" },
  { key: "nav.about", href: "/about" },
];

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [visible, setVisible] = useState(true);
  const [langOpen, setLangOpen] = useState(false);
  const lastScrollY = useRef(0);
  const pathname = usePathname();
  const { locale, setLocale } = useLocale();
  const langRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const scrollingDown = currentScrollY > lastScrollY.current;

      if (scrollingDown && currentScrollY > 50) {
        setVisible(false);
      } else if (!scrollingDown) {
        setVisible(true);
      }

      lastScrollY.current = currentScrollY;
      setScrolled(currentScrollY > 50);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === "Escape") { setIsOpen(false); setLangOpen(false); }
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(e.target as Node)) {
        setLangOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    if (isOpen) {
      document.addEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "";
    };
  }, [isOpen, handleKeyDown]);

  useEffect(() => {
    setIsOpen(false);
  }, [pathname]);

  const isActive = (href: string) => {
    if (href === "/") return pathname === "/";
    return pathname.startsWith(href);
  };

  const hasDarkHero =
    pathname === "/" ||
    pathname === "/about";
  const useLightText = hasDarkHero && !scrolled;

  return (
    <nav
      className={`fixed top-3 left-3 right-3 z-50 transition-all duration-500 ease-in-out rounded-2xl safe-area-top flex items-center ${
        visible ? "translate-y-0" : "-translate-y-[calc(100%+12px)]"
      } ${
        scrolled ? "pt-8 pb-3 px-4" : "bg-transparent pt-10 pb-5 px-4"
      }`}
    >
        {/* Desktop globe dropdown */}
        <div ref={langRef} className="hidden md:relative md:flex">
          <button
            type="button"
            onClick={() => setLangOpen(!langOpen)}
            className={clsx(
              "flex items-center justify-center w-9 h-9 rounded-lg transition-colors",
              useLightText ? "text-white/80 hover:text-white hover:bg-white/10" : "text-slate-500 hover:text-brand-600 hover:bg-slate-100",
              langOpen && (useLightText ? "bg-white/10 text-white" : "bg-slate-100 text-brand-600"),
            )}
            title={t(locale, "nav.switchLanguage")}
          >
            <Globe className="w-4 h-4" />
          </button>
          {langOpen && (
            <div className="absolute top-full left-0 mt-2 w-40 bg-white rounded-xl shadow-xl border border-slate-200 py-1.5 z-50">
              <button
                type="button"
                onClick={() => { setLocale("id"); setLangOpen(false); }}
                className={clsx(
                  "w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors",
                  locale === "id" ? "bg-brand-50 text-brand-700 font-semibold" : "text-slate-700 hover:bg-slate-50",
                )}
              >
                🇮🇩 <span>Indonesia</span>
                {locale === "id" && <span className="ml-auto text-brand-600 text-xs">✓</span>}
              </button>
              <button
                type="button"
                onClick={() => { setLocale("en"); setLangOpen(false); }}
                className={clsx(
                  "w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors",
                  locale === "en" ? "bg-brand-50 text-brand-700 font-semibold" : "text-slate-700 hover:bg-slate-50",
                )}
              >
                🇬🇧 <span>English</span>
                {locale === "en" && <span className="ml-auto text-brand-600 text-xs">✓</span>}
              </button>
            </div>
          )}
        </div>

        {/* Logo - always visible */}
        <Link
          href="/"
          className={`flex items-center text-3xl font-bold font-display tracking-tight transition-colors duration-500 group shrink-0 ${
            useLightText ? "text-white" : "text-brand-900"
          }`}
        >
          Citra Marvella<span className="text-gold-500">.</span>
        </Link>

        {/* Desktop nav links + CTA — right side */}
        <div className="ml-auto hidden md:flex items-center gap-6 lg:gap-7">
          {navLinks.map((link) => (
            <Link
              key={link.key}
              href={link.href}
              className={`text-sm lg:text-base font-medium transition-colors duration-500 relative group shrink-0 ${
                useLightText
                  ? "text-white/85 hover:text-white"
                  : "text-slate-600 hover:text-brand-600"
              }`}
            >
              {t(locale, link.key)}
              <span
                className={`absolute -bottom-1 left-0 h-0.5 bg-gold-500 transition-all ${
                  isActive(link.href) ? "w-full" : "w-0 group-hover:w-full"
                }`}
              />
            </Link>
          ))}
          <Link
            href="/export-inquiry"
            className={`shrink-0 flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 ${
              useLightText
                ? "bg-gold-500 text-brand-950 hover:bg-gold-400 shadow-lg shadow-gold-500/20"
                : "bg-gold-500 text-brand-950 hover:bg-gold-400 shadow-md shadow-gold-500/15"
            }`}
          >
            <Send className="w-3.5 h-3.5" />
            {t(locale, "nav.inquiry")}
          </Link>
        </div>

        {/* Spacer (mobile only) */}
        <div className="flex-1 md:hidden" />

        {/* Mobile — compact flags inline */}
        <div className="md:hidden flex items-center gap-1.5">
          <button
            type="button"
            onClick={() => setLocale("id")}
            className={clsx(
              "px-1.5 py-1 rounded text-base leading-none transition-opacity",
              locale === "id" ? "opacity-100" : "opacity-40 hover:opacity-70",
            )}
            title={t(locale, "nav.langIndonesia")}
          >🇮🇩</button>
          <span className={useLightText ? "text-white/20" : "text-slate-300"}>|</span>
          <button
            type="button"
            onClick={() => setLocale("en")}
            className={clsx(
              "px-1.5 py-1 rounded text-base leading-none transition-opacity",
              locale === "en" ? "opacity-100" : "opacity-40 hover:opacity-70",
            )}
            title={t(locale, "nav.langEnglish")}
          >🇬🇧</button>
        </div>

        {/* Mobile Toggle */}
        <button
          className={`md:hidden focus:outline-none transition-colors duration-500 min-h-[44px] min-w-[44px] flex items-center justify-center ${
            useLightText ? "text-white" : "text-slate-800"
          }`}
          onClick={() => setIsOpen(!isOpen)}
          aria-label={isOpen ? t(locale, "nav.closeMenu") : t(locale, "nav.openMenu")}
          aria-expanded={isOpen}
        >
          {isOpen ? <X size={24} /> : <Menu size={24} />}
        </button>

      {/* Mobile Backdrop */}
      <div
        className={`md:hidden fixed inset-0 z-40 bg-black/30 transition-opacity duration-300 ease-in-out ${
          isOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
        onClick={() => setIsOpen(false)}
        aria-hidden="true"
      />

      {/* Mobile Menu */}
      <div
        className={`md:hidden fixed top-[108px] left-3 right-3 z-40 bg-white/95 backdrop-blur-xl border-t border-slate-100 shadow-xl transition-all duration-300 ease-in-out safe-area-bottom rounded-b-2xl ${
          isOpen ? "translate-y-0 opacity-100" : "-translate-y-4 opacity-0 pointer-events-none"
        }`}
      >
        <div className="flex flex-col p-6 space-y-1">
          {navLinks.map((link) => (
            <Link
              key={link.key}
              href={link.href}
              className="text-lg font-medium text-slate-800 hover:text-brand-600 hover:bg-brand-50 transition-colors rounded-lg px-4 py-3 min-h-[44px] flex items-center"
              onClick={() => setIsOpen(false)}
            >
              {t(locale, link.key)}
            </Link>
          ))}
          <Link
            href="/export-inquiry"
            className="flex items-center justify-center gap-2 bg-gold-500 text-brand-950 px-4 py-3 min-h-[48px] rounded-lg text-base font-medium hover:bg-gold-400 transition-colors mt-3"
            onClick={() => setIsOpen(false)}
          >
            <Send className="w-4 h-4" />
            {t(locale, "nav.sendInquiry")}
          </Link>
          <div className="flex items-center justify-center gap-2 mt-3 pt-3">
            <button
              type="button"
              onClick={() => { setLocale("id"); setIsOpen(false); }}
              className={clsx(
                "flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors",
                locale === "id"
                  ? "bg-brand-50 text-brand-700 font-semibold"
                  : "text-slate-500 hover:bg-slate-50",
              )}
            >
              🇮🇩 Indonesia
            </button>
            <span className="text-slate-200">|</span>
            <button
              type="button"
              onClick={() => { setLocale("en"); setIsOpen(false); }}
              className={clsx(
                "flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors",
                locale === "en"
                  ? "bg-brand-50 text-brand-700 font-semibold"
                  : "text-slate-500 hover:bg-slate-50",
              )}
            >
              🇬🇧 English
            </button>
          </div>
        </div>
      </div>
    </nav>
  );
}
